import { isNil } from 'lodash';

import { AssetContainerEventType } from './asset-container-event';
import { AssetVisitType, isLiveAssetVisitType } from './asset-visit';
import { Maybe } from './globals';
import { isNilOrBlankString } from './js-helpers/str';
import { SPOT_STATUSES_RESERVED, SpotStatus } from './yard';

/**
 * Returns the first candidate that is a non-nil, non-blank string.
 * `isNilOrBlankString` is not a type predicate, so the cast is intentional
 * after the runtime guard.
 */
function firstNonBlankString(...candidates: Array<string | null | undefined>): string | null {
  for (const candidate of candidates) {
    if (!isNilOrBlankString(candidate)) {
      return candidate as string;
    }
  }
  return null;
}

/**
 * Returns true when the spot is reserved. Derived purely from `Spot.status`
 * (RESERVED_MANUAL or RESERVED_AUTOMATED); the eagerly-loaded `spotReserves`
 * relation and the `isActive` soft-delete flag are never consulted here.
 */
export function isSpotReserved(spot: { status?: Maybe<SpotStatus> }): boolean {
  return SPOT_STATUSES_RESERVED.includes(spot.status ?? SpotStatus.UNKNOWN);
}

/**
 * Joins a field (refNumber or confirmationNumber) from both primary and
 * secondary appointment into a single comma-separated string.
 * Returns null if no values are present.
 */
export function getAppointmentFieldValue(
  visit:
    | {
        appointment?: { refNumber?: string; confirmationNumber?: number | string } | null;
        secondaryAppointment?: { refNumber?: string; confirmationNumber?: number | string } | null;
      }
    | null
    | undefined,
  field: 'refNumber' | 'confirmationNumber'
): string | null {
  if (!visit) {
    return null;
  }

  const raw = [visit.appointment?.[field], visit.secondaryAppointment?.[field]];

  const values = raw
    .filter(v => {
      if (typeof v === 'number') {
        return true;
      }
      return typeof v === 'string' && v.trim() !== '';
    })
    .map(v => String(v).trim());

  return values.length > 0 ? values.join(', ') : null;
}

/**
 * Minimal shape for deriving the drop-off company name.
 * Works with both backend entities and frontend API objects.
 */
export interface IDropCompanyInput {
  dropAppointment?: { id?: string; user?: { company?: { name?: string } } } | null;
  assetContainerEvents?: Array<{
    eventType: string;
    assetVisit?: { company?: { name?: string }; companyHint?: string } | null;
  }> | null;
}

/**
 * Derives the drop-off carrier company name for an asset container:
 * 1. If parent visit is a live type, returns null (carrier stays with truck)
 * 2. If container has a drop appointment with a company name, returns that name
 * 3. Falls back to the company/companyHint of the visit on the Arrived event that has one
 */
export function getDropCompanyName(
  container: IDropCompanyInput | null | undefined,
  visitType?: AssetVisitType | string | null
): string | null {
  if (!container) {
    return null;
  }

  if (visitType && isLiveAssetVisitType(visitType as AssetVisitType)) {
    return null;
  }

  if (container.dropAppointment?.id) {
    const dropAppointmentCompanyName = firstNonBlankString(
      container.dropAppointment.user?.company?.name
    );
    if (dropAppointmentCompanyName) {
      return dropAppointmentCompanyName;
    }
  }

  const arrivedEvent = container.assetContainerEvents?.find(
    event => event.eventType === AssetContainerEventType.Arrived && !isNil(event.assetVisit)
  );
  if (arrivedEvent?.assetVisit) {
    return firstNonBlankString(
      arrivedEvent.assetVisit.company?.name,
      arrivedEvent.assetVisit.companyHint
    );
  }

  return null;
}

/**
 * Derives the pickup carrier company name from the pickup appointment.
 */
export function getPickupCompanyName(
  container:
    | {
        pickupAppointment?: { user?: { company?: { name?: string } } } | null;
      }
    | null
    | undefined
): string | null {
  if (!container) {
    return null;
  }
  return container.pickupAppointment?.user?.company?.name ?? null;
}
